import React, { useRef, useState, useEffect, useCallback, Fragment, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useMediaPipe } from "../../hooks/useMediaPipe";
import { useAudioProctoring } from "../../hooks/useAudioProctoring";
import type { HeadPose } from "../../hooks/useMediaPipe";
import { uploadPreAssessment, uploadAudioBaseline } from "../../api/proctor";
import { convertBlobToWav } from "../../utils/audioConverter";
import ProctoringInstructions from "./ProctoringInstructions";

type CaptureStep = "front" | "right" | "left" | "audio" | "id";

interface StepConfig {
  key: CaptureStep;
  label: string;
  instruction: string;
  subInstruction: string;
  requireFace: boolean;
  expectedPose: HeadPose | null;
  icon: string;
}

const STEPS: StepConfig[] = [
  {
    key: "front",
    label: "Front Face",
    instruction: "Look straight at the camera",
    subInstruction: "Keep your face centred and look directly ahead",
    requireFace: true,
    expectedPose: "front",
    icon: "🙂",
  },
  {
    key: "right",
    label: "Right Side",
    instruction: "Turn your head to the RIGHT",
    subInstruction: "Slowly turn your head to YOUR right until the indicator turns green",
    requireFace: true,
    expectedPose: "right",
    icon: "👈",
  },
  {
    key: "left",
    label: "Left Side",
    instruction: "Turn your head to the LEFT",
    subInstruction: "Slowly turn your head to YOUR left until the indicator turns green",
    requireFace: true,
    expectedPose: "left",
    icon: "👉",
  },
  {
    key: "id",
    label: "ID Card",
    instruction: "Hold your ID card up to the camera",
    subInstruction: "Place your ID card flat, centred inside the frame below",
    requireFace: false,
    expectedPose: null,
    icon: "🪪",
  },
  {
    key: "audio",
    label: "Audio Check",
    instruction: 'Ensure you are in a quiet environment',
    subInstruction: "Please note that this audio will be used to verify your environment and background noise while taking the assessment",
    requireFace: false,
    expectedPose: null,
    icon: "🎤",
  },
];

const HOLD_DURATION_MS = 1500;
const MAX_AUDIO_DURATION_S = 15;

export interface PreAssessmentProps {
  open: boolean;
  onClose: () => void;
  user_id: string;
  user_exam_id: string;
  assessment_id: string;
  candidate_name: string;
  onComplete: () => void;
  enableAudioCheck?: boolean; // Optional audio verification step
  hasSnapshots?: boolean;
  hasAudio?: boolean;
}

const PreAssessment: React.FC<PreAssessmentProps> = ({
  open,
  onClose,
  user_id,
  user_exam_id,
  assessment_id,
  candidate_name,
  onComplete,
  enableAudioCheck = true,
  hasSnapshots = false,
  hasAudio = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const { captureFrame, modelReady, modelError, faceStatus } = useMediaPipe(videoRef);
  const { startListening, stopListening, isListening, currentRms } = useAudioProctoring();
  const [showInstructions, setShowInstructions] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [captures, setCaptures] = useState<Record<string, Blob>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [idCaptureMode, setIdCaptureMode] = useState<"select" | "capture">("select");

  const activeSteps = useMemo(() => {
    if (hasSnapshots && hasAudio) return [];

    let steps = [...STEPS];
    if (hasSnapshots) {
      // Snapshots already done, only include audio if not done and enabled
      return (enableAudioCheck && !hasAudio) ? STEPS.filter(s => s.key === "audio") : [];
    }

    // Snapshots NOT done, include them
    steps = STEPS.filter(s => ["front", "right", "left", "id"].includes(s.key));

    // Add audio if not done and enabled
    if (enableAudioCheck && !hasAudio) {
      steps.push(STEPS.find(s => s.key === "audio")!);
    }

    return steps;
  }, [hasSnapshots, hasAudio, enableAudioCheck]);

  const currentStep = activeSteps[stepIndex];
  const isIdStep = currentStep?.key === "id";
  const isAudioStep = currentStep?.key === "audio";
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<any>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<any>(null);

  // Audio check state
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioCheckComplete, setAudioCheckComplete] = useState(false);
  const idFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setShowInstructions(true);
    } else {
      stopListening();
      setAudioPermissionGranted(false);
      setAudioCheckComplete(false);
    }
  }, [open, stopListening]);

  const holdStartRef = useRef<number | null>(null);
  const holdRafRef = useRef<number>(0);

  // ── Shutter sound ─────────────────────────────────────────────────────────
  const playShutter = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(1200, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);
      oscillator.onended = () => ctx.close();
    } catch {
      // AudioContext not available — silently skip
    }
  }, []);

  useEffect(() => {
    if (!open || showInstructions) return;
    let stream: MediaStream | null = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        setError("Camera access denied. Please allow camera permissions.");
      }
    })();
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open, showInstructions]);

  // Clean up previews on unmount
  useEffect(() => {
    return () => Object.values(previews).forEach((url) => URL.revokeObjectURL(url));
  }, [previews]);

  // Clean up audio on unmount or close
  useEffect(() => {
    if (open) {
      setShowInstructions(true);
    } else {
      stopListening();
      setAudioPermissionGranted(false);
      setAudioCheckComplete(false);
    }
  }, [open, stopListening]);

  // ── Mirror landmark canvas onto overlay ───────────────────────────────────
  useEffect(() => {
    if (!open || showInstructions) return;
    let rafId = 0;
    const paint = () => {
      const src = faceStatus.landmarkCanvas;
      const dst = overlayCanvasRef.current;
      if (src && dst && src.width > 0) {
        dst.width = src.width;
        dst.height = src.height;
        const ctx = dst.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, dst.width, dst.height);
          ctx.drawImage(src, 0, 0);
        }
      }
      rafId = requestAnimationFrame(paint);
    };
    rafId = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(rafId);
  }, [open, showInstructions, faceStatus.landmarkCanvas]);

  // ── Image Processing & Step Navigation ────────────────────────────────────
  const finishImageStep = useCallback(async (blob: Blob, currentNextCaptures?: Record<string, Blob>) => {
    const step = currentStep;
    const previewUrl = URL.createObjectURL(blob);
    const next = currentNextCaptures || { ...captures, [step.key]: blob };

    // Update local state first
    setCaptures(next);
    setPreviews((prev) => ({ ...prev, [step.key]: previewUrl }));

    const isLastImageStep = step.key === "id" || (step.key !== "audio" && stepIndex === activeSteps.length - 1);

    if (isLastImageStep) {
      setUploading(true);
      try {
        await uploadPreAssessment(
          {
            user_id,
            user_exam_id,
            assessment_id,
            candidate_name,
            front: next.front,
            right: next.right,
            left: next.left,
            nin: next.id,
          },
          setProgress,
        );

        if (activeSteps.some(s => s.key === "audio")) {
          setUploading(false);
          setProgress(0);
          setStepIndex(stepIndex + 1);
        } else {
          onComplete();
        }
      } catch (err: any) {
        const serverError = err?.response?.data || err;
        const errorMessage = serverError?.message && serverError?.error
          ? `${serverError.error}: ${serverError.message}`
          : serverError?.message || serverError?.error || "Upload failed";
        setError(errorMessage);
        setUploading(false);
      }
    } else if (stepIndex < activeSteps.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  }, [captures, currentStep, stepIndex, activeSteps, user_id, user_exam_id, assessment_id, candidate_name, onComplete]);

  // ── Core capture logic ────────────────────────────────────────────────────
  const doCapture = useCallback(async () => {
    if (capturing) return;
    setError(null);
    setCapturing(true);
    holdStartRef.current = null;
    setHoldProgress(0);

    try {
      const step = currentStep;
      const { blob, faceCount, rejectReason } = await captureFrame(step.requireFace);

      if (!blob) {
        if (rejectReason === "too_dark") {
          setError("Image too dark. Please improve your lighting and try again.");
        } else if (rejectReason === "no_face" || faceCount === 0) {
          setError("No face detected. Make sure your face is clearly visible and well-lit.");
        } else if (rejectReason === "multiple_faces" || faceCount > 1) {
          setError(`${faceCount} faces detected. Only one person should be in the frame.`);
        } else {
          setError("Could not capture image. Please try again.");
        }
        return;
      }

      playShutter();
      await finishImageStep(blob);
    } finally {
      setCapturing(false);
    }
  }, [capturing, captureFrame, finishImageStep, playShutter, currentStep]);

  // ── ID Upload logic ───────────────────────────────────────────────────────
  const handleIdUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError("Please upload a valid image file (JPG, PNG).");
      return;
    }

    setCapturing(true);
    setError(null);

    try {
      // Draw to canvas to ensure consistent format
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = objectUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error("Canvas context error");
      ctx.drawImage(img, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9);
      });

      if (!blob) throw new Error("Conversion failed");

      URL.revokeObjectURL(objectUrl);
      await finishImageStep(blob);
    } catch (err) {
      console.log({ err, 'data': '123' });
      //console.error("ID upload error:", err);
      setError("Failed to process uploaded ID image. Please try again.");
    } finally {
      setCapturing(false);
      if (idFileInputRef.current) idFileInputRef.current.value = "";
    }
  }, [finishImageStep]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new (window as any).MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        try {
          const wavBlob = await convertBlobToWav(webmBlob);
          setAudioBlob(wavBlob);
        } catch (err) {
          console.error("Audio conversion failed:", err);
          setError("Failed to process audio. Please try again.");
        }
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
        if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingDuration(0);
      setError(null);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          if (prev >= MAX_AUDIO_DURATION_S - 1) {
            stopRecording();
            return MAX_AUDIO_DURATION_S;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setError("Failed to start recording. Please check microphone permissions.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const handleAudioUpload = useCallback(async () => {
    if (!audioBlob) return;
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const serverResponse = await uploadAudioBaseline({
        user_id,
        user_exam_id,
        assessment_id,
        audio_baseline: audioBlob
      }, setProgress);

      setAudioCheckComplete(true);
      sessionStorage.setItem(
        `audio_baseline_${user_exam_id}`,
        JSON.stringify({
          checked: true,
          timestamp: Date.now(),
          features: serverResponse?.features ?? null,
          baseline_url: serverResponse?.baseline_url ?? null,
        })
      );

      onComplete();
    } catch (err: any) {
      const serverError = err?.response?.data;
      const errorMessage = serverError?.error && serverError?.detail
        ? `${serverError.error} (${serverError.detail})`
        : serverError?.message || serverError?.error || "Audio upload failed";
      setError(errorMessage);
      setUploading(false);
    }
  }, [audioBlob, user_id, user_exam_id, assessment_id, onComplete]);

  const requestAudioPermission = useCallback(async () => {
    setError(null);
    try {
      await startListening();
      setAudioPermissionGranted(true);
    } catch (err) {
      setError("Microphone access denied. Please allow microphone permissions.");
      setAudioPermissionGranted(false);
    }
  }, [startListening]);

  useEffect(() => {
    if (currentStep?.key === "audio" && !audioPermissionGranted && !audioCheckComplete) {
      requestAudioPermission();
    }
  }, [stepIndex, audioPermissionGranted, audioCheckComplete, requestAudioPermission, currentStep]);

  const [holdProgress, setHoldProgress] = useState(0);

  useEffect(() => {
    if (!open || !modelReady || capturing || uploading) return;

    if (!currentStep || isIdStep || isAudioStep) return;
    if (captures[currentStep.key]) return;
    if (!currentStep.requireFace) return;

    const poseOk = faceStatus.count === 1 && faceStatus.pose === currentStep.expectedPose;

    if (!poseOk) {
      holdStartRef.current = null;
      setHoldProgress(0);
      cancelAnimationFrame(holdRafRef.current);
      return;
    }

    if (holdStartRef.current === null) {
      holdStartRef.current = performance.now();
    }

    const tick = () => {
      if (holdStartRef.current === null) return;
      const elapsed = performance.now() - holdStartRef.current;
      const pct = Math.min(100, (elapsed / HOLD_DURATION_MS) * 100);
      setHoldProgress(pct);
      if (pct < 100) {
        holdRafRef.current = requestAnimationFrame(tick);
      } else {
        doCapture();
      }
    };

    holdRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(holdRafRef.current);
    // eslint-disable-next-line
  }, [open, modelReady, capturing, uploading, stepIndex, faceStatus.count, faceStatus.pose, captures, enableAudioCheck]);

  const handleRetake = useCallback((index: number) => {
    const step = activeSteps[index];

    setCaptures((prev) => { const c = { ...prev }; delete c[step.key]; return c; });
    setPreviews((prev) => {
      if (prev[step.key]) URL.revokeObjectURL(prev[step.key]);
      const c = { ...prev }; delete c[step.key]; return c;
    });
    setStepIndex(index);
    setError(null);
    holdStartRef.current = null;
    setHoldProgress(0);
    if (step.key === "id") {
      setIdCaptureMode("select");
    }
    if (step.key === "audio") {
      setAudioPermissionGranted(false);
      setAudioCheckComplete(false);
      setAudioBlob(null);
      setRecordingDuration(0);
      stopListening();
    }
  }, [activeSteps, stopListening]);

  const poseMatched = isIdStep || isAudioStep
    ? true
    : faceStatus.count === 1 && faceStatus.pose === currentStep?.expectedPose;

  const faceIndicatorColor = !modelReady
    ? "#9ca3af"
    : faceStatus.count === 0
      ? "#ef4444"
      : faceStatus.count > 1
        ? "#f59e0b"
        : poseMatched
          ? "#22c55e"
          : "#87aa4b";

  const faceIndicatorText = !modelReady
    ? "Loading model…"
    : faceStatus.count === 0
      ? "No face detected"
      : faceStatus.count > 1
        ? `${faceStatus.count} faces — only 1 allowed`
        : poseMatched
          ? `${currentStep?.expectedPose?.toUpperCase()} — hold still…`
          : `Turn ${currentStep?.expectedPose === "right" ? "RIGHT →" : currentStep?.expectedPose === "left" ? "← LEFT" : "face FORWARD"}`;

  const completedCount = activeSteps.filter((s) => {
    if (s.key === "audio") return !!audioBlob;
    return !!captures[s.key];
  }).length;
  const progressPct = (completedCount / activeSteps.length) * 100;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-y-auto modal-proctor"
        style={{ zIndex: 99999 }}
        onClose={() => {
          if (!uploading) onClose();
        }}
      >
        <div className="flex items-center justify-center min-h-screen px-4 py-6">
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Modal panel */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className={`relative bg-white rounded-2xl shadow-2xl w-full h-full ${showInstructions ? 'max-w-6xl' : 'max-w-5xl'} mx-auto overflow-hidden transform transition-all`}>
              {showInstructions ? (
                <ProctoringInstructions
                  onProceed={() => {
                    setShowInstructions(false)
                    document.querySelector(".modal-proctor")?.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                  onClose={onClose}
                  hasSnapshots={hasSnapshots}
                  hasAudio={hasAudio}
                />
              ) : (
                <>
                  <div className="bg-primary px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Dialog.Title className="text-white text-lg font-semibold">
                          Identity Verification
                        </Dialog.Title>
                        <p className="text-white/80 text-sm mt-0.5">
                          Complete all steps before starting your assessment
                        </p>
                      </div>
                      {!uploading && (
                        <button
                          onClick={onClose}
                          className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-b border-gray-100">
                    {activeSteps.map((s, i) => {
                      const captured = !!captures[s.key];
                      const isActive = i === stepIndex;
                      return (
                        <div key={s.key} className="flex flex-col items-center flex-1">
                          <div className="flex items-center w-full">
                            {i > 0 && (
                              <div className={`flex-1 h-0.5 ${i <= completedCount ? "bg-primary" : "bg-gray-200"}`} />
                            )}
                            <div
                              className={`
                            relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300
                            ${captured
                                  ? "bg-primary text-white shadow-md shadow-primary/30"
                                  : isActive
                                    ? "bg-primary text-white shadow-md shadow-primary/30 ring-4 ring-primary/20"
                                    : "bg-gray-200 text-gray-500"
                                }
                          `}
                            >
                              {captured ? (
                                previews[s.key] ? (
                                  <img
                                    src={previews[s.key]}
                                    alt={s.label}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )
                              ) : (
                                <span className="text-base">{s.icon}</span>
                              )}
                              {captured && previews[s.key] && (
                                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            {i < STEPS.length - 1 && (
                              <div className={`flex-1 h-0.5 ${i < completedCount ? "bg-primary" : "bg-gray-200"}`} />
                            )}
                          </div>
                          <span className={`text-xs mt-1.5 font-medium ${captured ? "text-primary" : isActive ? "text-primary" : "text-gray-400"}`}>
                            {s.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className={`px-6 ${isAudioStep ? 'py-8' : 'py-4'}`}>
                    <div className="text-center mb-3">
                      <p className="text-gray-800 font-semibold text-base">
                        {currentStep.instruction}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        {currentStep.subInstruction}
                      </p>
                    </div>

                    {/* Video area */}
                    <div className={`relative rounded-xl overflow-hidden bg-black mx-auto ${isAudioStep ? 'aspect-video max-h-[600px]' : 'aspect-video max-h-[480px]'
                      }`}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full h-full object-cover ${isAudioStep ? "opacity-0" : ""}`}
                      />

                      {!isIdStep && (
                        <canvas
                          ref={overlayCanvasRef}
                          className="absolute inset-0 w-full h-full pointer-events-none"
                        />
                      )}

                      {isIdStep && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div
                            className="relative border-2 border-dashed border-white/80 rounded-xl"
                            style={{ width: "75%", paddingTop: "47%" }}
                          >
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-white/90 text-sm bg-black/40 px-3 py-1 rounded-full">
                                Align ID card here
                              </span>
                            </div>
                            {/* Corner markers */}
                            <div className="absolute top-0 left-0 w-5 h-5 border-t-3 border-l-3 border-white rounded-tl-md" />
                            <div className="absolute top-0 right-0 w-5 h-5 border-t-3 border-r-3 border-white rounded-tr-md" />
                            <div className="absolute bottom-0 left-0 w-5 h-5 border-b-3 border-l-3 border-white rounded-bl-md" />
                            <div className="absolute bottom-0 right-0 w-5 h-5 border-b-3 border-r-3 border-white rounded-br-md" />
                          </div>
                          {/* Dimmed outside area */}
                          <div className="absolute inset-0 bg-black/30 pointer-events-none" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, 12.5% 26.5%, 12.5% 73.5%, 87.5% 73.5%, 87.5% 26.5%, 12.5% 26.5%)" }} />
                        </div>
                      )}

                      {/* Face status badge */}
                      {!isIdStep && !isAudioStep && (
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium">
                          <span
                            className="w-2.5 h-2.5 rounded-full animate-pulse"
                            style={{ backgroundColor: faceIndicatorColor }}
                          />
                          {faceIndicatorText}
                        </div>
                      )}

                      {/* Hidden File Input */}
                      <input
                        ref={idFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleIdUpload}
                        className="hidden"
                      />

                      {/* ID Select Mode Overlay */}
                      {isIdStep && idCaptureMode === "select" && (
                        <div className="absolute inset-0 bg-gray-900 z-20 flex flex-col items-center justify-center p-6 text-center">
                          <h3 className="text-white text-xl font-semibold mb-6">How would you like to provide your ID?</h3>
                          <div className="flex flex-col sm:flex-row w-full gap-4 max-w-md">
                            <button
                              onClick={() => setIdCaptureMode("capture")}
                              className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white py-6 px-4 rounded-xl flex flex-col items-center gap-3 transition-all"
                            >
                              <span className="text-4xl">📷</span>
                              <span className="font-medium text-lg">Capture ID</span>
                            </button>
                            <button
                              onClick={() => idFileInputRef.current?.click()}
                              className="flex-1 bg-primary text-white hover:bg-primary/90 py-6 px-4 rounded-xl flex flex-col items-center gap-3 transition-all shadow-lg"
                            >
                              <span className="text-4xl">📤</span>
                              <span className="font-medium text-lg">Upload ID</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ID Upload Trigger (when in capture mode) */}
                      {isIdStep && idCaptureMode === "capture" && !capturing && !uploading && (
                        <div className="absolute z-10 inset-0 bg-transparent flex flex-col items-center justify-end pb-8">
                          <button
                            onClick={() => idFileInputRef.current?.click()}
                            className="bg-white/90 hover:bg-white text-primary px-6 py-2.5 rounded-xl font-bold shadow-2xl flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95"
                          >
                            <span>📤 Click to upload ID instead</span>
                          </button>
                        </div>
                      )}

                      {/* Step counter badge */}
                      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium">
                        Step {stepIndex + 1}/{activeSteps.length}
                      </div>

                      {/* Audio check overlay */}
                      {isAudioStep && (
                        <div className="absolute inset-0 border border-primary backdrop-blur-sm flex flex-col items-center justify-center p-10">
                          <div className="text-center space-y-6 max-w-2xl w-full">
                            <div className={`text-7xl mb-3 ${recording ? 'animate-pulse text-red-500' : 'text-primary'}`}>
                              {recording ? '🔴' : '🎤'}
                            </div>
                            <h3 className="text-white font-bold text-3xl">
                              {recording ? 'Recording Baseline...' : audioBlob ? 'Recording Captured' : 'Audio Verification'}
                            </h3>

                            {!recording && !audioBlob && (
                              <div className="space-y-6">
                                <p className="text-white/90 text-lg leading-relaxed">
                                  We need to record a short audio baseline. Please ensure you're in a quiet environment when you start recording.
                                </p>
                                <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                                  <p className="text-white font-medium text-xl italic">
                                    "{currentStep.instruction.replace('Say: ', '')}"
                                  </p>
                                </div>
                                <button
                                  onClick={startRecording}
                                  className="mx-auto px-8 py-4 bg-primary text-white rounded-xl text-lg font-semibold hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-3"
                                >
                                  <span>🎤 Start Recording</span>
                                </button>
                              </div>
                            )}

                            {recording && (
                              <div className="space-y-6">
                                <div className="flex items-center justify-center gap-4">
                                  <div className="w-4 h-4 bg-red-500 rounded-full animate-ping" />
                                  <span className="text-white font-mono text-4xl">
                                    00:{recordingDuration.toString().padStart(2, '0')}
                                    <span className="text-white/30 text-2xl ml-1">/ {MAX_AUDIO_DURATION_S}s</span>
                                  </span>
                                </div>

                                <div className="flex items-center justify-center gap-3 h-20">
                                  {[...Array(12)].map((_, i) => {
                                    const vol = currentRms * 100;
                                    const h = Math.max(15, Math.min(80, vol * (0.8 + Math.random() * 0.4)));
                                    return (
                                      <div
                                        key={i}
                                        className="w-3 bg-red-500 rounded-full transition-all duration-75"
                                        style={{ height: `${h}%` }}
                                      />
                                    );
                                  })}
                                </div>

                                <button
                                  onClick={stopRecording}
                                  className="mx-auto px-8 py-4 bg-white text-red-600 rounded-xl text-lg font-semibold hover:bg-red-50 transition-all duration-200 shadow-lg"
                                >
                                  Stop Recording
                                </button>
                              </div>
                            )}

                            {audioBlob && !recording && (
                              <div className="space-y-6">
                                <div className="text-green-400 text-6xl">✓</div>
                                <p className="text-white font-medium text-xl">Recording captured and ready for upload</p>

                                {error && (
                                  <div className="mx-auto max-w-md bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-100 text-sm">
                                    <p className="font-bold mb-1">Upload Failed</p>
                                    <p>{error}</p>
                                  </div>
                                )}
                                {uploading && (
                                  <div className="mt-4">
                                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1.5">
                                      <span>Uploading verification data…</span>
                                      <span className="font-medium">{progress}%</span>
                                    </div>
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary rounded-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-center justify-center gap-4">
                                  <button
                                    disabled={uploading}
                                    onClick={() => { setAudioBlob(null); setRecordingDuration(0); setError(null); }}
                                    className="px-6 py-3 border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium hover:bg-white/10 transition-all"
                                  >
                                    ↺ Re-record
                                  </button>
                                  {!error && <button
                                    onClick={handleAudioUpload}
                                    disabled={uploading}
                                    className="px-8 py-3 bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-bold hover:bg-primary/80 transition-all shadow-lg"
                                  >
                                    Finalize & Submit
                                  </button>}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Hold progress bar */}
                      {holdProgress > 0 && holdProgress < 100 && (
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-3/5">
                          <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-400 rounded-full"
                              style={{ width: `${holdProgress}%`, transition: "width 0.05s linear" }}
                            />
                          </div>
                          <p className="text-center text-white/80 text-xs mt-1">Hold still…</p>
                        </div>
                      )}

                      {/* Captured flash */}
                      {holdProgress >= 100 && capturing && (
                        <div className="absolute inset-0 bg-white/20 flex items-center justify-center">
                          <span className="text-white text-lg font-bold drop-shadow-lg">
                            ✓ Captured!
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Error message */}
                    {(modelError || error) && (
                      <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{modelError ? `Model error: ${modelError}. Try refreshing.` : error}</span>
                      </div>
                    )}

                    {/* Upload progress */}
                    {uploading && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-1.5">
                          <span>Uploading verification data…</span>
                          <span className="font-medium">{progress}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Footer ── */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    {/* Hint text */}
                    {!uploading && !capturing && (
                      <p className="text-center text-gray-500 text-sm mb-3">
                        {isAudioStep
                          ? audioCheckComplete
                            ? "Audio check complete! Click Continue to proceed."
                            : audioPermissionGranted
                              ? "Microphone is ready. Click Continue when you're ready."
                              : "Click 'Grant Permission' to allow microphone access"
                          : isIdStep
                            ? idCaptureMode === "select"
                              ? "Please choose how you want to provide your ID"
                              : "Position your ID card inside the frame and click the button below"
                            : modelReady
                              ? poseMatched
                                ? "Hold still — capturing automatically…"
                                : `${currentStep?.instruction}. We'll capture automatically when detected.`
                              : "Loading face detection model…"}
                      </p>
                    )}

                    <div className="flex items-center gap-3">
                      {/* Cancel/Back button */}
                      {!uploading && (
                        <button
                          onClick={() => {
                            if (isIdStep && idCaptureMode === "capture") {
                              setIdCaptureMode("select");
                            } else {
                              onClose();
                            }
                          }}
                          className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                        >
                          {isIdStep && idCaptureMode === "capture" ? "Back" : "Cancel"}
                        </button>
                      )}

                      {/* ID capture button */}
                      {isIdStep && idCaptureMode === "capture" && !uploading && (
                        <button
                          onClick={doCapture}
                          disabled={capturing}
                          className={`flex-[2] py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${capturing
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/30 hover:shadow-lg"
                            }`}
                        >
                          {capturing ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Capturing…
                            </span>
                          ) : (
                            "📷 Capture ID Card"
                          )}
                        </button>
                      )}

                      {/* Audio continue button */}
                      {/* {isAudioStep && !uploading && audioPermissionGranted && (
                    <button
                      onClick={completeAudioCheck}
                      disabled={audioCheckComplete}
                      className={`flex-[2] py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${audioCheckComplete
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90 shadow-md shadow-primary/30 hover:shadow-lg"
                        }`}
                    >
                      ✓ Continue
                    </button>
                  )} */}

                      {/* Retake buttons for completed steps */}
                      {!isIdStep && !isAudioStep && !uploading && completedCount > 0 && captures[currentStep?.key] && (
                        <button
                          onClick={() => handleRetake(stepIndex)}
                          className="flex-1 py-2.5 px-4 border border-orange-300 text-orange-600 rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors"
                        >
                          Retake
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PreAssessment;
